import { store, computed, observable, action, event } from '~/common/mobx.decorator'
import { appStore, notifyStore, routerStore, authStore } from '~/stores'
import { EVENTS, PATHS } from '~/common/constants'
import { apolloClient, GET_LEFT_SIDE_RECRUITER_STATISTICS_QUERY, GET_USER_INFO_QUERY, SIGNOUT_RECRUITER_MUTATION } from '~/common/apollo'
import { eventBus } from 'mobx-event-bus2'
import {
	ApplicantSourcesSvg,
	CandidatesSvg,
	ChatNavSvg,
	DashboardSvg,
	JobsSvg,
	MailV2Svg,
	NotificationSvg,
	ReportSvg,
	SearchSvg,
	SettingsSvg,
	UserSettingSvg,
	UserSvg,
} from '~/components/icons'
import { recruiterRoleStore } from '~/features/recruiter-role/recruiter-role.store'
import { COPY as NOTIFICATION_COPY } from '~/features/recruiter-notifications/notification.constants'
import { formatNumber } from '~/shared/utils/formatNumber'
import { maestroVersionStore } from '~/features/recruiter-v2/shell/maestro-version.store'
import { v2RedirectTarget } from '~/features/recruiter-v2/shell/maestro-version.helper'

const FEATURE_TYPE = {
	default: 'default',
	common: 'common',
}

@store()
class CareStore {
	@observable showProfileSidebar
	@observable customHeader = {}
	@observable statistics = {}
	@observable customBreadcumbs = {}
	@observable customHeaderNav

	@computed
	get activeStatistic() {
		const statistic = this.statistics[this.activeFeature?.code]
		return statistic ? formatNumber(statistic, true) : statistic
	}

	@action
	fetchStatistics = async () => {
		const response = await apolloClient().query({
			query: GET_LEFT_SIDE_RECRUITER_STATISTICS_QUERY,
		})
		this.statistics = response?.data?.statistics || {}
		this.setStatistics('recruiter-jobs', response?.data?.statistics?.jobsTotal)
		this.setStatistics('recruiter-candidates', response?.data?.statistics?.workersTotal)
		this.setStatistics('recruiter-role', response?.data?.statistics?.employersTotal)
	}

	@action
	setStatistics = (type, data) => {
		this.statistics[type] = data
	}

	getFeature = (type) => {
		// Notifications are readable by every recruiter on a flag-on tenant, so they open Configuration too.
		const canAccessConfiguration = authStore.canReadCadences || authStore.canAccessRoleScreen || authStore.canReadNotifications
		const defaultMenu = [
			{
				// Maestro V2 (beta): the redesigned screens under /v2. It carries its own
				// shell (sidebar, top bar, version switch), so it opts out of this
				// layout and out of both menus.
				//
				// Dark until the tenant has `maestro_v2_release`. `permission` is what
				// `features` filters on and what the router builds from, so a tenant
				// without the flag has no /v2 route at all rather than a reachable URL
				// behind a hidden menu item.
				code: 'recruiter-v2',
				name: 'MAESTRO_V2',
				path: PATHS.recruiter.v2,
				icon: DashboardSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: false,
				permission: appStore.enableMaestroV2,
				breadcrumbPath: PATHS.recruiter.v2,
			},
			{
				code: 'recruiter-configuration',
				name: 'Configuration',
				path: PATHS.recruiter.configuration,
				icon: SettingsSvg,
				auth: true,
				// Same rule as the sidebar: hidden while viewing as another recruiter.
				showAppBar: canAccessConfiguration && !recruiterRoleStore.impersonatedRecruiter,
				showSideBar: canAccessConfiguration && !recruiterRoleStore.impersonatedRecruiter,
				useLayout: true,
				permission: canAccessConfiguration,
				authorizationFeature: authStore.canReadCadences ? 'recruiter-cadences' : 'recruiter-role',
				eventId: 'configuration-nav_btn',
			},
			// Kept as a direct route for deep links. It intentionally has no sidebar
			// entry; Configuration is the single administrative navigation surface.
			{
				code: 'recruiter-cadences',
				name: 'Workflows',
				path: PATHS.recruiter.cadences,
				icon: SettingsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				// `maestro_email_workflows` decides whether the route exists at all;
				// CareRoute redirects deep links to Configuration when it is off.
				permission: appStore.enableEmailWorkflows,
				breadcrumbPath: PATHS.recruiter.configuration,
				breadcrumbParentName: 'Configuration',
			},
			{
				code: 'recruiter-cadence-templates',
				name: 'Message templates',
				path: PATHS.recruiter.cadence_templates,
				icon: SettingsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				// Match the workflow route: the shared authorization feature is the
				// source of truth. The async capabilities query must not break deep links.
				// The tenant flag, which is loaded before routes render, still gates it.
				permission: appStore.enableEmailWorkflows,
				authorizationFeature: 'recruiter-cadences',
				breadcrumbPath: PATHS.recruiter.configuration,
				breadcrumbParentName: 'Configuration',
			},
			{
				// Notification settings hub. `maestro_notification_settings` decides whether the route exists at all;
				// CareRoute redirects deep links to Configuration when it is off. Who may read or edit is the page's
				// own check (authStore.notificationCapabilities), so an async capabilities answer never breaks a deep link.
				code: 'recruiter-notifications',
				// The breadcrumb: "Configuration › Automated messages" (amendment A15; the route and code keep "notifications").
				name: NOTIFICATION_COPY.pageTitle,
				path: PATHS.recruiter.notifications,
				icon: SettingsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				permission: appStore.enableNotificationSettings,
				breadcrumbPath: PATHS.recruiter.configuration,
				breadcrumbParentName: 'Configuration',
			},
			{
				// Smart communication S4: opened from the Workflows list, so its breadcrumb leads back there.
				code: 'recruiter-cadence-hot-candidates',
				name: 'Hot candidates',
				path: PATHS.recruiter.cadence_hot_candidates,
				icon: SettingsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				permission: appStore.enableEmailWorkflows,
				authorizationFeature: 'recruiter-cadences',
				breadcrumbPath: PATHS.recruiter.cadences,
				breadcrumbParentName: 'Workflows',
			},
			{
				// Smart communication S3: opened from the Workflows list, so its breadcrumb leads back there.
				code: 'recruiter-cadence-segments',
				name: 'Segments',
				path: PATHS.recruiter.cadence_segments,
				icon: SettingsSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				permission: appStore.enableEmailWorkflows,
				authorizationFeature: 'recruiter-cadences',
				breadcrumbPath: PATHS.recruiter.cadences,
				breadcrumbParentName: 'Workflows',
			},
			{
				code: 'recruiter-home',
				name: 'DASHBOARD',
				path: PATHS.recruiter.home,
				icon: DashboardSvg,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				useLayout: true,
				eventId: 'dashboard-nav_btn',
				permission: true,
				breadcrumbPath: PATHS.recruiter.home,
			},
			{
				code: 'recruiter-jobs',
				name: 'JOBS',
				path: PATHS.recruiter.recruiter_jobs,
				icon: JobsSvg,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				useLayout: true,
				permission: true,
				statistic: this.statistics?.jobsTotal,
				eventId: 'jobs-nav_btn',
				subCode: 'recruiter-job-info',
			},
			{
				code: 'recruiter-candidates',
				name: 'CANDIDATES',
				path: PATHS.recruiter.candidates,
				icon: CandidatesSvg,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				useLayout: true,
				permission: true,
				statistic: this.statistics?.workersTotal,
				eventId: 'travelers-nav_btn',
			},
			{
				// Directly under Candidates: same audience (Admin / Account Manager) uses
				// this when the weekly NEW APP LIST lands. Gated per-tenant via
				// `maestro_candidate_app_access` so rollout is client-by-client.
				code: 'recruiter-candidate-access',
				name: 'CANDIDATE_APP_ACCESS',
				path: PATHS.recruiter.candidate_access,
				icon: MailV2Svg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				permission: appStore.enableCandidateAppAccess && authStore.canAccessRoleScreen,
			},
			{
				// Recruiter ↔ candidate messaging. The flag decides whether the route
				// exists at all, same as Earnings below. `statistic` is the number of
				// conversations with unread messages, fed by messagesStore.
				code: 'recruiter-messages',
				name: 'MESSAGES_NAV',
				path: PATHS.recruiter.messages,
				icon: ChatNavSvg,
				auth: true,
				showAppBar: appStore.enableInAppMessage,
				showSideBar: appStore.enableInAppMessage,
				useLayout: true,
				permission: appStore.enableInAppMessage,
				statistic: this.statistics?.unreadConversations,
				eventId: 'messages-nav_btn',
			},
			{
				code: 'care-pay-matrix',
				name: 'EARNINGS',
				path: PATHS.recruiter.pay_matrix,
				icon: ReportSvg,
				auth: true,
				showAppBar: true,
				showSideBar: appStore.enableOpusMatchPay,
				useLayout: true,
				// The flag decides whether this entry exists at all, not merely
				// whether it is visible: `features` filters on `permission`, and the
				// router builds its routes from that same list. A tenant without
				// Opus Match pay has no route to reach, not a hidden link.
				permission: appStore.enableOpusMatchPay,
				eventId: 'pay-matrix-nav_btn',
			},
			{
				code: 'recruiter-timecard-approval',
				name: 'TIMECARD_APPROVALS',
				path: PATHS.recruiter.timecard_approval,
				icon: ReportSvg,
				auth: true,
				showAppBar: true,
				showSideBar: appStore.enableOpusMatchPay,
				useLayout: true,
				permission: appStore.enableOpusMatchPay,
				eventId: 'timecard-approval-nav_btn',
			},
			{
				code: 'recruiter-role',
				name: 'USER_ROLES',
				path: PATHS.recruiter.role,
				icon: UserSettingSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				permission: authStore.canAccessRoleScreen,
				statistic: this.statistics?.employersTotal,
			},
			{
				// Reached by clicking a name on the User Roles table, never from the
				// nav — no sidebar or app-bar entry. Same permission as the table it
				// is opened from: `features` filters on `permission` and the router
				// builds its routes from that same list, so a user who cannot open
				// User Roles has no user-detail route to reach either.
				code: 'recruiter-user-info',
				name: 'USER_ROLES',
				path: PATHS.recruiter.user_info,
				icon: UserSettingSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				permission: authStore.canAccessRoleScreen,
				breadcrumbPath: PATHS.recruiter.role,
			},
			{
				code: 'recruiter-report',
				name: 'REPORTS_AND_ANALYTICS',
				path: PATHS.recruiter.report,
				icon: ReportSvg,
				auth: true,
				showAppBar: true,
				showSideBar: true,
				useLayout: true,
				// One entry for every report: with `maestro_applicant_source_tracking` the dashboards and
				// Applicant sources are tabs of one page that titles itself. Without it, Reports is exactly as it was.
				permission: appStore.enableReportAndAnalytics || appStore.enableApplicantSourceTracking,
				ownPageTitle: Boolean(appStore.enableApplicantSourceTracking),
				eventId: 'recruiter-report-nav_btn',
			},
			{
				// SPT-2326: was its own page for a day, now a tab of Reports & Analytics. Kept only so old
				// links redirect there; no sidebar or app-bar entry.
				code: 'recruiter-applicant-sources',
				name: 'APPLICANT_SOURCES',
				path: PATHS.recruiter.applicant_sources,
				icon: ApplicantSourcesSvg,
				ownPageTitle: true,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				permission: appStore.enableApplicantSourceTracking,
				eventId: 'recruiter-applicant-sources-nav_btn',
			},
			{
				code: 'recruiter-profile',
				name: 'USER_PROFILE_BREADCRUMB',
				path: PATHS.recruiter.user_profile,
				icon: UserSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				permission: true,
				eventId: 'recruiter-profile-nav_btn',
			},
			{
				// Job alerts (SPT-2331): opened from the account menu, which shows the entry only when the client has
				// job-area alerts on (jobAreaAlertsEnabled). The route always exists; the screens say so when it is off.
				code: 'recruiter-job-alerts',
				name: 'JOB_AREA_ALERTS_TITLE',
				path: PATHS.recruiter.job_alerts,
				icon: NotificationSvg,
				// The pages title themselves (list: "Job alerts"; form: Back + "New job alert"), as they do in V2.
				ownPageTitle: true,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				permission: true,
				breadcrumbPath: PATHS.recruiter.job_alerts,
			},
			{
				// Reached from the search box, never from the nav — no sidebar or
				// app-bar entry. The flag decides whether the route exists at all:
				// `features` filters on `permission` and the router builds from that
				// same list, so a tenant without global search has nothing to reach.
				code: 'global-search',
				name: 'GLOBAL_SEARCH_RESULTS_TITLE',
				path: PATHS.recruiter.search,
				// Every other feature gives the breadcrumb an icon; this one shipped with
				// an empty string, and `Breadcrumbs` renders the glyph only when there is
				// one — which is why /search was the single screen whose breadcrumb was
				// bare text.
				icon: SearchSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				permission: appStore.enableGlobalSearch,
				breadcrumbPath: PATHS.recruiter.home,
			},
			{
				code: 'recruiter-job-info',
				name: 'JOBS',
				path: PATHS.recruiter.job_info,
				icon: '',
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				permission: true,
				eventId: 'jobs-nav_btn',
				breadcrumbPath: PATHS.recruiter.recruiter_jobs,
			},
			{
				code: 'recruiter-candidate-info',
				name: 'CANDIDATE',
				path: PATHS.recruiter.candidate_info,
				icon: CandidatesSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				permission: true,
				breadcrumbPath: PATHS.recruiter.candidates,
			},
			{
				code: 'notification',
				name: 'DASHBOARD',
				path: PATHS.recruiter.notification,
				icon: DashboardSvg,
				auth: true,
				showAppBar: false,
				showSideBar: false,
				useLayout: true,
				permission: true,
				breadcrumbPath: PATHS.recruiter.home,
			},
		].filter(Boolean)

		const common = [
			{
				code: 'common-signin',
				name: 'SIGNIN',
				path: PATHS.common.signin,
				icon: 'user',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				useLayout: false,
			},
			{
				code: 'common-reset',
				name: 'RESET',
				path: PATHS.common.reset,
				icon: 'user',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: false,
			},
			{
				code: 'common-change-password',
				name: 'REFER_TRAVELER',
				path: PATHS.common.change_password,
				icon: 'user',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: false,
			},
			{
				code: 'common-errors',
				name: 'ERRORS',
				path: PATHS.common.errors,
				icon: 'user',
				auth: false,
				showAppBar: false,
				showSideBar: false,
				isDisabled: false,
				useLayout: false,
			},
			{
				code: 'common-unsubscribe-email',
				name: 'UNSUBSCRIBE_EMAIL',
				path: PATHS.common.unsubscribe_email,
				icon: null,
				auth: false,
				showAppBar: false,
				showSideBar: false,
				useLayout: false,
			},
		].filter(Boolean)

		let result = null
		switch (type) {
			case FEATURE_TYPE.default:
				result = defaultMenu
				break
			case FEATURE_TYPE.common:
				result = common
				break
			default:
				result = []
				break
		}
		return result
	}

	/**
	 * Whether this recruiter works in V2: the tenant has released it and they have
	 * not chosen Classic. Unknown until the profile is in (the choice is per user),
	 * and then Classic — the redirect below catches up once it is known.
	 */
	@computed
	get usesMaestroV2() {
		return Boolean(appStore.enableMaestroV2 && authStore.authorization && authStore.id) && !maestroVersionStore.prefersClassic
	}

	/** Where a signed-in recruiter lands from `/` (and so right after signing in). */
	@computed
	get defaultHomePath() {
		return this.usesMaestroV2 ? PATHS.recruiter.v2 : PATHS.recruiter.home
	}

	/** The V2 address to send a Classic URL to, or null to stay (see v2RedirectTarget). */
	v2RedirectFor = (pathname = '', search = '') => v2RedirectTarget(pathname, search, { usesV2: this.usesMaestroV2, embedded: routerStore.isEmbedded })

	@computed
	get features() {
		const defaultMenu = this.getFeature(FEATURE_TYPE.default)?.filter((feature) => feature.permission)

		return [...defaultMenu]
	}

	@computed
	get allFeatures() {
		const common = this.getFeature(FEATURE_TYPE.common)
		const allFeature = [...this.features, ...common]

		return allFeature?.find((feature) => feature.path === routerStore.basePath)
	}

	@computed
	get activeFeature() {
		return this.features?.find((feature) => feature.path === routerStore.basePath)
	}

	@computed
	get code() {
		return this.activeFeature?.code
	}

	@computed
	get name() {
		return this.activeFeature?.name || ''
	}

	@computed
	get useLayout() {
		return this.activeFeature?.useLayout
	}

	@computed
	get sidebarMenu() {
		// With Applicant sources (SPT-2326) Reports sits just above Configuration, as in V2; without the flag, as before.
		const order = appStore.enableApplicantSourceTracking
			? ['recruiter-home', 'recruiter-jobs', 'recruiter-candidates', 'recruiter-messages', 'recruiter-report', 'recruiter-configuration']
			: ['recruiter-home', 'recruiter-jobs', 'recruiter-candidates', 'recruiter-report', 'recruiter-messages', 'recruiter-configuration']
		return this.features
			?.filter((feature) => feature.showSideBar)
			.sort((left, right) => {
				const leftIndex = order.indexOf(left.code)
				const rightIndex = order.indexOf(right.code)
				return (leftIndex < 0 ? order.length : leftIndex) - (rightIndex < 0 ? order.length : rightIndex)
			})
	}

	@computed
	get appbarMenu() {
		// The phone tab bar shows the first four and puts the rest under "More", so
		// the order is by how often a recruiter on a phone needs each one.
		const order = ['recruiter-home', 'recruiter-jobs', 'recruiter-candidates', 'recruiter-messages', 'recruiter-report', 'recruiter-configuration']
		return this.features
			?.filter((feature) => feature.showAppBar)
			.sort((left, right) => {
				const leftIndex = order.indexOf(left.code)
				const rightIndex = order.indexOf(right.code)
				return (leftIndex < 0 ? order.length : leftIndex) - (rightIndex < 0 ? order.length : rightIndex)
			})
	}

	@action
	toogleProfileSidebar = async () => {
		this.showProfileSidebar = !this.showProfileSidebar
	}

	@action
	getProfileInfo = async () => {
		try {
			const data = await apolloClient().query({
				query: GET_USER_INFO_QUERY,
			})

			if (data?.employer) {
				this.profile = data.employer
				this.roles = data.employer?.roles
			}
		} catch (err) {
			console.error(err)
		}
	}

	@action
	logout = async () => {
		this.showProfileSidebar = false

		const response = await apolloClient().mutate({
			mutation: SIGNOUT_RECRUITER_MUTATION,
		})

		const success = response.data?.employerSignOut
		if (success) notifyStore.success('$MESSAGES.SIGNED_OUT')

		eventBus.post(EVENTS.authStore.logout)
		routerStore.goPage(PATHS.common.signin)
	}

	@action
	handleClickChangePassword = async () => {
		this.showProfileSidebar = false
		await routerStore.goPage(PATHS.common.change_password)
	}

	@event(EVENTS.careStore.updateHeader)
	async onUpdateHeader({ payload }) {
		this.customHeader = payload
	}

	@event(EVENTS.careStore.updateHeaderNav)
	async onUpdateHeaderNav({ payload }) {
		this.customHeaderNav = payload
	}

	@event(EVENTS.careStore.updateBreadcumbs)
	async onUpdateBreadcumbs({ payload }) {
		this.customBreadcumbs = payload
	}
}

export const careStore = new CareStore()
